import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useConfig } from './ConfigContext';
import { api } from '../utils/api';
import { TIER_RULES as FALLBACK_TIER_RULES, getTierRules as getFallbackTierRules, isDimensionAllowed as fallbackIsDimensionAllowed, canSelectDimensions as fallbackCanSelectDimensions, ALL_DIMENSIONS } from '../utils/tierRules';

const SubscriptionContext = createContext();

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

export function SubscriptionProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const { config, getTierRules: getConfigTierRules } = useConfig();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usageInfo, setUsageInfo] = useState(null);

  // Get current tier
  const tier = user?.accountTier || 'anonymous';

  // Helper: Get tier rules from config or fallback
  const getTierRules = useCallback((targetTier) => {
    // Try to get from server config first
    const serverRules = getConfigTierRules?.(targetTier);
    if (serverRules) {
      return serverRules;
    }
    // Fallback to client rules
    return getFallbackTierRules(targetTier);
  }, [getConfigTierRules]);

  // Helper: Check if dimension is allowed
  const isDimensionAllowed = useCallback((dimension, targetTier) => {
    const rules = getTierRules(targetTier);
    if (!rules) return fallbackIsDimensionAllowed(dimension, targetTier);
    
    return rules.allowedDimensions.includes(dimension) || 
           rules.allowedDimensions === ALL_DIMENSIONS ||
           (Array.isArray(rules.allowedDimensions) && rules.allowedDimensions.length === 24);
  }, [getTierRules]);

  // Helper: Check if dimensions can be selected
  const canSelectDimensions = useCallback((selectedDimensions, targetTier) => {
    const rules = getTierRules(targetTier);
    if (!rules) return fallbackCanSelectDimensions(selectedDimensions, targetTier);
    
    // Check if too many dimensions selected
    if (selectedDimensions.length > rules.maxDimensions) {
      return {
        allowed: false,
        reason: `Maximum ${rules.maxDimensions} dimensions allowed for ${targetTier} tier`
      };
    }
    
    // Check if all dimensions are allowed
    const disallowed = selectedDimensions.filter(d => !isDimensionAllowed(d, targetTier));
    if (disallowed.length > 0) {
      return {
        allowed: false,
        reason: `Dimensions not allowed: ${disallowed.join(', ')}`,
        disallowed
      };
    }
    
    return { allowed: true };
  }, [getTierRules, isDimensionAllowed]);

  // Fetch subscription status
  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/api/payments/status');
      setSubscriptionStatus(response.subscription);
      // Update usage info from the response
      if (response.usage) {
        setUsageInfo({
          roundsToday: response.usage.roundsToday,
          dailyLimit: response.usage.dailyLimit,
          remainingToday: response.usage.remainingToday
        });
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Check if user can play a round
  const canPlayRound = useCallback(() => {
    if (tier === 'paid') {
      return { canPlay: true, reason: 'paid_tier' };
    }

    // Free and anonymous users are handled by daily limit
    return { canPlay: true, reason: 'within_limits' };
  }, [tier]);

  // Check if dimensions array is valid
  const validateDimensions = useCallback((dimensions) => {
    const result = canSelectDimensions(dimensions, tier);
    if (!result.allowed) {
      return {
        valid: false,
        error: result.reason,
        disallowed: result.disallowed
      };
    }
    return { valid: true };
  }, [tier, canSelectDimensions]);


  // Upgrade to Pro
  const upgradeAccount = useCallback(async (plan) => {
    try {
      const response = await api.post('/api/payments/create-checkout-session', {
        type: 'subscription',
        plan
      });

      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Failed to upgrade account:', error);
      throw error;
    }
  }, []);

  // Open customer portal
  const openPortal = useCallback(async () => {
    try {
      const response = await api.post('/api/payments/create-portal-session');
      
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Failed to open portal:', error);
      throw error;
    }
  }, []);

  // Update usage info after round
  const updateUsageInfo = useCallback((info) => {
    setUsageInfo(info);
  }, []);

  // Check if dimension is allowed for current tier
  const canAccessDimension = useCallback((dimension) => {
    return isDimensionAllowed(dimension, tier);
  }, [tier, isDimensionAllowed]);

  // Get tier benefits
  const getTierBenefits = useCallback((targetTier) => {
    const currentRules = getTierRules(tier);
    const targetRules = getTierRules(targetTier);
    const benefits = [];

    if (targetRules.dailyRounds > currentRules.dailyRounds) {
      if (targetRules.dailyRounds >= 999999) {
        benefits.push('Unlimited rounds per day');
      } else {
        benefits.push(`${targetRules.dailyRounds} rounds per day`);
      }
    }

    const targetDimCount = Array.isArray(targetRules.allowedDimensions) 
      ? targetRules.allowedDimensions.length 
      : 24;
    const currentDimCount = Array.isArray(currentRules.allowedDimensions)
      ? currentRules.allowedDimensions.length
      : 0;

    if (targetDimCount > currentDimCount) {
      benefits.push(`All ${targetDimCount} dimensions unlocked`);
    }

    if (targetRules.maxDimensions > currentRules.maxDimensions) {
      if (targetRules.maxDimensions >= 999) {
        benefits.push('Combine unlimited dimensions');
      }
    }

    if (targetTier === 'paid') {
      benefits.push('Unlimited progress history');
      benefits.push('Advanced analytics');
      benefits.push('Premium content access');
    }

    return benefits;
  }, [tier, getTierRules]);

  const value = {
    // Tier info
    tier,
    isAnonymous: tier === 'anonymous',
    isFree: tier === 'free',
    isPaid: tier === 'paid',
    
    // Status
    subscriptionStatus,
    usageInfo,
    loading,
    
    // Functions
    canAccessDimension,
    canPlayRound,
    validateDimensions,
    upgradeAccount,
    openPortal,
    fetchStatus,
    updateUsageInfo,
    getTierBenefits,
    
    // Rules
    tierRules: getTierRules(tier)
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

